import { useState, useEffect, useRef } from 'react';

/**
 * Hook to add a minimum display time for loading states (e.g., skeletons)
 * to avoid flickering when data loads too quickly.
 * 
 * @param {boolean} isLoading - The actual loading state from data fetching
 * @param {number} minDisplayMs - Minimum time (in ms) to show the loading state (default: 300)
 * @returns {boolean} - The delayed loading state
 * 
 * @example
 * const { data, isLoading } = getData('/api/endpoint');
 * const showSkeleton = useDelayedLoading(isLoading, 300);
 * 
 * return showSkeleton ? <Skeleton /> : <Content data={data} />;
 */
export function useDelayedLoading(isLoading, minDisplayMs = 300) {
    // Initialize showLoading with the initial isLoading value to avoid flash on first render
    const [showLoading, setShowLoading] = useState(isLoading);
    const loadStartTimeRef = useRef(isLoading ? Date.now() : null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        // When loading starts
        if (isLoading && !loadStartTimeRef.current) {
            loadStartTimeRef.current = Date.now();
            setShowLoading(true);
        }

        // When loading finishes
        if (!isLoading && loadStartTimeRef.current) {
            const elapsed = Date.now() - loadStartTimeRef.current;
            const remainingDelay = Math.max(0, minDisplayMs - elapsed);

            if (remainingDelay > 0) {
                // Wait the remaining time before hiding the loading state
                timeoutRef.current = setTimeout(() => {
                    setShowLoading(false);
                    loadStartTimeRef.current = null;
                }, remainingDelay);
            } else {
                // Loading took longer than minimum, hide immediately
                setShowLoading(false);
                loadStartTimeRef.current = null;
            }
        }

        // Cleanup timeout on unmount or when isLoading changes
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isLoading, minDisplayMs]);

    return showLoading;
}
